import * as THREE from "three";
import { ShadowCadence } from "./shadowCadence";
import { SeeThrough } from "./seeThrough";
import { Cell, Grid } from "@highmarch/domain";
import { IsoCamera } from "./isoCamera";
import { SurroundView } from "./views/surround";
import { GroundMesh } from "./groundMesh";
import { groundAt } from "./terrain";
import { BloomOnScale, GraphicsPrefs, effectiveTier, setAppliedTier } from "./graphicsPrefs";
import { PostPlan, QualityGovernor, QualityTier, defaultTier, followsDefaultTier, pinnedTier, postNeedsRebuild, postPlan, sameTier, startingTier, tierStoreFor, vetoFxaa } from "./quality";
import { PostFx } from "./postfx";
import { installCheapSoftShadows } from "./shadowFilter";
import { ShadowProxies } from "./shadowProxies";
import { HqMaterials } from "./hqMaterials";
import { onSettingsChange, reducedVfx } from "../ui/settings";
import { BOSS_DUSK, DEFAULT_LOOK, LOOKS, Look, SUN_BREAK, buildEnvironment } from "./look";
import { BossDusk } from "./bossDusk";
import { RareGlow } from "./rareLook";
import { CloudShadows } from "./cloudShadows";
import { StrikeLight } from "./strikeLight";
import { consoleLogger } from "../infra/adapters";
import { reloadFor } from "../infra/reloadNote";
import { frameJournal } from "../infra/frameJournal";
import { CpuRelease } from "./cpuRelease";
import { glContexts } from "../infra/glContexts";
import { breadcrumbs } from "../infra/breadcrumbs";
import { mainRest } from "./mainRest";

/** Cells are centred on integer XZ coordinates; y is up. */
export function cellToWorld(c: Cell, y = 0): THREE.Vector3 { return new THREE.Vector3(c.x, y, c.y); }

export const Palette = {
  ground: 0x5c8a4e, blocked: 0x4d4844, designed: 0x9e7546, tail: 0x857057, spawn: 0x8c3399, base: 0x3373d9,
  grunt: 0xbf3833, runner: 0xf28c26, brute: 0x732626, boss: 0x1a0d1a, archer: 0xd9cc8c, cannon: 0x595966, frost: 0x8cd9f2,
  ghost: 0xf2d94d, ghostEnd: 0xff9f2e, ghostHint: 0xcfd8dc, fire: 0xe65c1f, lightning: 0xe6d94d, ballista: 0x8c6b47, poison: 0x6bb33d, beacon: 0xf2e6a6, mortar: 0x7a6a5a, barracks: 0xa8483c, arcane: 0xd84fb0, hex: 0x8a4fd0, valid: 0x4de680, hp: 0x40d060,
  groundFar: 0x6f9a55, water: 0x2b3c5e, fog: 0xc8d4de, sky: 0xb9c8e6,
} as const;

/** Renderer plus the isometric camera; input handlers forward to the pure IsoCamera. */
export class SceneRig {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene = new THREE.Scene();
  readonly iso = new IsoCamera();
  private sun!: THREE.DirectionalLight;
  private fill!: THREE.DirectionalLight;
  private hemi!: THREE.HemisphereLight;
  private sky: THREE.Mesh | null = null;
  private post: PostFx | null = null;
  look: Look = LOOKS[DEFAULT_LOOK];
  get camera(): THREE.PerspectiveCamera { return this.iso.camera; }

  constructor(readonly canvas: HTMLCanvasElement) {
    installCheapSoftShadows();                               // before any program compiles
    // No MSAA on the canvas: the frame always goes through the post chain (setPost), whose scene target carries the
    // tier's MSAA (or FXAA in its final pass: postfx.ts), and the canvas only ever receives its one full-screen quad.
    const start = startingTier();
    // The pixel ratio is the tier's (applyTier: its pixelRatio, capped by the device's own); the perf overlay's res
    // button overrides it live.
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: false, powerPreference: "high-performance" });
    glContexts.open("main");
    this.renderer.info.autoReset = false;
    GroundMesh.maxSamplers = this.renderer.capabilities.maxTextures;   // the per-program fragment sampler limit (MAX_TEXTURE_IMAGE_UNITS)
    this.renderer.setClearColor(Palette.sky);
    this.scene.fog = new THREE.Fog(Palette.sky, 60, 160);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    ShadowProxies.install(this.renderer);                    // the towers' reduced casters (views/towerLod.ts)
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.05;
    // Lighting rig from art/SHADING.md §3: one warm key with soft shadows from front-right-top (screen terms — the
    // camera orbits on the −x/−z side, so that is world −x, up, slightly −z), a cool-sky / violet-ground hemisphere,
    // no ambient on top, and fog that melts the island's far edge into the sky (distances follow the camera, see update).
    const sun = new THREE.DirectionalLight(0xfff1dc, 1.6);
    sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    sun.shadow.camera.near = 1;
    sun.shadow.camera.far = 60;
    sun.shadow.camera.left = sun.shadow.camera.bottom = -14;
    sun.shadow.camera.right = sun.shadow.camera.top = 14;
    sun.shadow.bias = -0.0005;
    sun.shadow.normalBias = 0.02;
    sun.shadow.radius = 4;
    this.scene.add(sun, sun.target);
    this.sun = sun;
    this.fill = new THREE.DirectionalLight(0x9fb8ff, 0);
    this.scene.add(this.fill, this.fill.target);
    this.hemi = new THREE.HemisphereLight(0xcfe3ff, 0x6b5a8a, 0.55);
    this.scene.add(this.hemi);
    this.resize();
    window.addEventListener("resize", this.resize);
    // The window "resize" event does not cover every way the canvas changes size: on iOS the page can lay out after
    // the renderer sized itself (the hub once rendered a 675x337 buffer stretched over a portrait screen). Watching
    // the canvas itself catches all of them.
    if (typeof ResizeObserver === "function") { this.resizeObserver = new ResizeObserver(() => this.resize()); this.resizeObserver.observe(canvas); }
    canvas.addEventListener("webglcontextlost", this.onContextLost);
    canvas.addEventListener("webglcontextrestored", this.onContextRestored);
    // A `?q=<tier>` pin (captures) locks the tier — the governor ships locked anyway (quality.ts) — and saves nothing.
    this.quality = new QualityGovernor(start, (t) => this.applyTier(t), (s) => this.post?.setRenderScale(s), tierStoreFor());
    if (pinnedTier() !== null) this.quality.locked = true;     // …even with `?autoq`
    // Only reduced effects changes the picture; the other settings (audio, volume, perf overlay…) must not re-apply
    // the tier, which would undo the perf overlay's manual switches and `?lite` and reset bloom mid boss dusk.
    this.reducedVfxApplied = reducedVfx();
    this.unsubscribe.push(onSettingsChange(() => { if (reducedVfx() !== this.reducedVfxApplied) this.applyReducedVfx(); }));
    // Settings → Graphics: the preset is the governor's tier; the overrides are folded in by applyTier. A preset pick
    // applies through the governor (bindPreset's `set`/`useDefault`) before the listeners run, so the listener only
    // re-applies when the overrides changed what the renderer should do.
    this.quality.followsDefault = followsDefaultTier();
    GraphicsPrefs.bindPreset({
      index: () => this.quality.index, set: (i) => this.quality.set(i),
      followsDefault: () => this.quality.followsDefault, useDefault: () => this.quality.useDefault(), defaultIndex: () => defaultTier(),
    });
    this.unsubscribe.push(GraphicsPrefs.onChange(() => {
      if (!this.tier || !sameTier(effectiveTier(this.quality.current), this.tier)) this.applyTier(this.quality.current);
    }));
    this.setLook(this.look);
  }
  private resizeObserver: ResizeObserver | null = null;
  private readonly unsubscribe: (() => void)[] = [];
  private reducedVfxApplied = false;

  /**
   * Applies a look (render/look.ts): key/fill/hemisphere, the image-based light, sky and fog, ground variation,
   * material depth, and the post chain. Switchable live (`__td.game.rig.setLook(LOOKS.goldenHour)`) for side-by-sides.
   */
  setLook(look: Look): void {
    this.look = look;
    this.blendTo = null; this.blendT = 0;
    this.renderer.toneMapping = look.toneMapping === "neutral" ? THREE.NeutralToneMapping : THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = look.exposure;
    this.sun.color.set(look.sun.color); this.sun.intensity = look.sun.intensity;
    this.fill.color.set(look.fill.color); this.fill.intensity = look.fill.intensity;
    this.hemi.color.set(look.hemi.sky); this.hemi.groundColor.set(look.hemi.ground); this.hemi.intensity = look.hemi.intensity;
    this.scene.environment?.dispose();
    this.scene.environment = look.envIntensity > 0 ? buildEnvironment(this.renderer, look) : null;
    HqMaterials.applyLook(look.envIntensity, look.normalScale);
    HqMaterials.groundVar.value = look.groundVariation; HqMaterials.groundWarm.value.set(look.groundWarm); HqMaterials.groundCool.value.set(look.groundCool);
    this.renderer.setClearColor(look.sky);

    (this.scene.fog as THREE.Fog).color.set(look.fog);
    this.setSky(look);
    this.sunYaw = NaN; this.placeSun();
    this.post?.setLook(look, this.aoAllowed());
  }

  /** Render quality (quality.ts): the chosen tier; automatic stepping only with `?autoq`. */
  quality!: QualityGovernor;
  private tier: QualityTier | null = null;
  private aoAllowed(): boolean { return !reducedVfx() && (this.tier?.ao ?? true); }

  private applyTier(preset: QualityTier): void {
    const t = effectiveTier(preset);
    this.tier = t;
    setAppliedTier(t);
    this.reducedVfxApplied = reducedVfx();
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, t.pixelRatio));
    const wantShadowType = t.softShadows ? THREE.PCFSoftShadowMap : THREE.PCFShadowMap;
    if (this.renderer.shadowMap.type !== wantShadowType || this.sun.shadow.mapSize.x !== t.shadowSize || this.shadowsOn !== t.shadows) {
      this.renderer.shadowMap.type = wantShadowType;
      this.sun.shadow.mapSize.set(t.shadowSize, t.shadowSize);
      this.sun.shadow.map?.dispose();
      this.sun.shadow.map = null;                          // re-allocated at the new size on the next render
      this.setShadows(t.shadows);
    }
    this.bloomScale = t.bloom;
    // MSAA is fixed per PostFx (the scene target's sample count is set at allocation): rebuilt on a change (setPost).
    const plan = postPlan(t, reducedVfx());
    this.setPost(plan);
    this.setBloom(plan.bloomScale > 0, true);
    this.post?.setLook(this.look, this.aoAllowed());
    this.resize();
  }
  private bloomScale = 1;
  private bloomWanted = false;

  /** Reduced effects toggled: only what it governs — bloom and AO — follows; resolution, shadows and MSAA stay. */
  private applyReducedVfx(): void {
    this.reducedVfxApplied = reducedVfx();
    const plan = postPlan(this.effective(), this.reducedVfxApplied);
    this.setBloom(plan.bloomScale > 0, true);
    this.post?.setLook(this.look, this.aoAllowed());
  }
  /** The tier the renderer runs: the applied one, or the governor's with the overrides before the first apply. */
  private effective(): QualityTier { return this.tier ?? effectiveTier(this.quality.current); }

  /**
   * The sky: a gradient dome with a sun and slow high clouds, behind everything and outside the fog.
   *
   * Drawn LAST among the opaques, not first (lighting review quick win): its fragment shader is the most expensive
   * per pixel in the scene (four-octave fbm three times over, plus three ridge loops), and drawn first it shaded the
   * whole screen before the terrain covered ~80 % of it. Its vertices sit on the far plane (xyww) and depth test with
   * LessEqual against a cleared depth of 1.0, so drawn last it only shades the pixels where nothing else was drawn —
   * the band above the horizon. It writes no depth, and transparent objects (VFX, haze-less sprites) still draw over
   * it because they come after every opaque.
   */
  private setSky(look: Look): void {
    if (!this.sky) {
      const mat = new THREE.ShaderMaterial({
        side: THREE.BackSide, depthWrite: false, fog: false,
        uniforms: { uTime: HqMaterials.time, uTop: { value: new THREE.Color() }, uHorizon: { value: new THREE.Color() }, uSun: { value: new THREE.Color() }, uSunDir: { value: new THREE.Vector3(0, 1, 0) } },
        vertexShader: `varying vec3 vDir; void main() { vDir = normalize(position); vec4 p = projectionMatrix * modelViewMatrix * vec4(position, 1.0); gl_Position = p.xyww; }`,
        fragmentShader: `varying vec3 vDir; uniform float uTime; uniform vec3 uTop, uHorizon, uSun, uSunDir;
          float h(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
          float n(vec2 p) { vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f); return mix(mix(h(i), h(i + vec2(1, 0)), f.x), mix(h(i + vec2(0, 1)), h(i + vec2(1, 1)), f.x), f.y); }
          float fbm(vec2 p) { return n(p) * 0.52 + n(p * 2.03 + 3.7) * 0.26 + n(p * 4.1 + 9.1) * 0.14 + n(p * 8.3 + 1.3) * 0.08; }
          void main() {
            float y = max(0.0, vDir.y);
            float az = atan(vDir.x, vDir.z);
            vec3 c = mix(uHorizon, uTop, pow(y, 0.55));
            float s = max(0.0, dot(normalize(vDir), uSunDir));
            c += uSun * (pow(s, 180.0) * 2.5 + pow(s, 6.0) * 0.28);

            // Cumulus. The camera looks DOWN at 35 degrees, so the only sky on screen is the band just above the
            // horizon — an empty gradient there is a third of the frame doing nothing. The deck is projected onto a
            // plane well above the eye, which is what gives the clouds perspective: big and separated overhead,
            // crowded and flattened as they run away toward the horizon.
            vec2 cp = vec2(vDir.x, vDir.z) / max(y + 0.055, 0.055);
            vec2 drift = vec2(uTime * 0.004, uTime * 0.0026);
            float deck = fbm(cp * 0.22 + drift);
            float wisp = fbm(cp * 0.55 + drift * 1.7 + 5.3);
            float body = smoothstep(0.48, 0.72, deck * 0.78 + wisp * 0.22);
            float edge = smoothstep(0.40, 0.66, deck * 0.78 + wisp * 0.22) - body;
            // lit from the sun side: sample the field a little toward the sun and take the difference as a normal
            vec2 toSun = normalize(vec2(uSunDir.x, uSunDir.z) + 1e-4);
            float lit = fbm(cp * 0.22 + drift + toSun * 0.5) - deck;
            vec3 cloudLit = mix(vec3(1.0), uSun, 0.35) * (1.02 + lit * 2.6);
            vec3 cloudDark = mix(uHorizon, uTop, 0.5) * 0.86;
            vec3 cloud = mix(cloudDark, cloudLit, clamp(0.45 + lit * 3.2, 0.0, 1.0));
            float deckMask = smoothstep(0.012, 0.09, y) * (1.0 - smoothstep(0.55, 0.95, y));
            c = mix(c, cloud, clamp(body * 0.86 + edge * 0.3, 0.0, 1.0) * deckMask);

            // high cirrus above the deck
            vec2 cip = vec2(az * 2.2, y * 9.0) + vec2(uTime * 0.01, 0.0);
            float cl = n(cip) * 0.55 + n(cip * 2.3 + 4.1) * 0.3 + n(cip * 5.1 + 8.7) * 0.15;
            c = mix(c, mix(c, vec3(1.0), 0.7), smoothstep(0.54, 0.72, cl) * smoothstep(0.3, 0.6, y) * 0.45);

            // three ranges of distant peaks, each paler and hazier than the one in front of it
            for (int k = 0; k < 3; k++) {
              float fk = float(k);
              float base = 0.028 + fk * 0.026;
              float amp = 0.030 + fk * 0.020;
              float ridge = base + amp * (n(vec2(az * (3.0 + fk * 2.5) + fk * 17.0, fk)) * 0.62 + n(vec2(az * (8.0 + fk * 4.0) + fk * 5.0, fk + 1.0)) * 0.38);
              vec3 col = mix(uHorizon, uTop, 0.30 + fk * 0.14) * (0.90 - fk * 0.13);
              float m = 1.0 - smoothstep(ridge - 0.0035, ridge + 0.0035, vDir.y);
              // snow on the tops of the nearest range, haze eating the feet of all of them
              if (k == 2) col = mix(col, mix(col, vec3(1.0), 0.5), smoothstep(ridge - 0.016, ridge - 0.002, vDir.y));
              c = mix(c, col, m * (0.88 - fk * 0.16));
            }
            c = mix(c, uHorizon, smoothstep(0.03, -0.08, vDir.y) * 0.9);   // haze swallows everything below the horizon
            gl_FragColor = vec4(c, 1.0);
          }`,
      });
      this.sky = new THREE.Mesh(new THREE.SphereGeometry(400, 32, 16), mat);
      this.sky.renderOrder = SceneRig.SkyRenderOrder;
      this.sky.frustumCulled = false;
      this.scene.add(this.sky);
    }
    const u = (this.sky.material as THREE.ShaderMaterial).uniforms;
    (u.uTop.value as THREE.Color).set(look.skyTop ?? look.envSky); (u.uHorizon.value as THREE.Color).set(look.sky); (u.uSun.value as THREE.Color).set(look.envSun);
    this.scene.background = null;
  }

  /** After every opaque object (see setSky); transparent objects draw after all opaque ones regardless. */
  static readonly SkyRenderOrder = 1000;

  /** Bloom is part of the look (glow slots, VFX) but costs fill rate: off under reduced effects. */
  setBloom(on: boolean, fromTier = false): void {
    if (!fromTier && this.quality) this.quality.locked = true;   // a manual toggle from the overlay sticks
    this.bloomWanted = on;
    this.post?.setBloomScale(on ? Math.max(this.bloomScale, SceneRig.ManualBloomScale) : 0);
  }
  /** Bloom scale when the overlay turns bloom on in a tier that has it off (the same as the Graphics override's). */
  static readonly ManualBloomScale = BloomOnScale;

  /** The post chain, (re)built when the tier's MSAA changes: a render target's sample count is fixed at creation. */
  private postBuilt: { msaa: number } | null = null;
  private postFxaa = false;
  private setPost(plan: PostPlan): void {
    this.postFxaa = plan.fxaa;
    if (this.still) return;                                  // resting: `wake` builds it
    if (!postNeedsRebuild(this.postBuilt, plan) && this.post) { this.post.setFxaa(plan.fxaa); return; }
    this.post?.dispose();
    this.postBuilt = { msaa: plan.msaa };
    this.post = new PostFx(this.renderer, this.scene, this.camera, this.canvas.clientWidth || window.innerWidth, this.canvas.clientHeight || window.innerHeight, 0.55, false, plan.msaa, plan.fxaa);
    this.post.setRenderScale(this.quality?.scale ?? 1);
    this.post.setBloomScale(this.bloomWanted ? this.bloomScale : 0);
    this.post.setLook(this.look, this.aoAllowed());
    if (this.lut) this.post.setLut(this.lut, this.lutMix);
    this.healthDue = 2;
  }

  /**
   * Shader health, checked a couple of frames after the post chain changes and then every few seconds: any program
   * three could not link (`diagnostics` is set on failure) is reported here for the perf overlay, since a phone has
   * no console. A failure while FXAA is on turns FXAA off and rebuilds — that is the one pass whose loss blacks the
   * whole screen — so a compile difference between desktop and iOS degrades the picture instead of removing it.
   */
  shaderErrors: string[] = [];
  private healthDue = 2;
  private healthEvery = 0;
  private checkShaderHealth(): void {
    if (this.healthDue > 0) { this.healthDue--; if (this.healthDue > 0) return; } else if (++this.healthEvery < SceneRig.HealthEveryFrames) return;
    this.healthEvery = 0;
    const failed: string[] = [];
    for (const p of this.renderer.info.programs ?? []) {
      const d = (p as unknown as { diagnostics?: { programLog?: string; fragmentShader?: { log?: string }; vertexShader?: { log?: string } } }).diagnostics;
      if (d) failed.push(`${p.name}: ${(d.fragmentShader?.log || d.vertexShader?.log || d.programLog || "link failed").slice(0, 160)}`);
    }
    this.shaderErrors = failed;
    if (failed.length > 0 && this.postFxaa) { vetoFxaa(); this.setPost(postPlan(this.effective(), reducedVfx())); }
  }
  static readonly HealthEveryFrames = 180;

  dispose(): void {
    window.removeEventListener("resize", this.resize);
    this.resizeObserver?.disconnect();
    this.canvas.removeEventListener("webglcontextlost", this.onContextLost);
    this.canvas.removeEventListener("webglcontextrestored", this.onContextRestored);
    if (this.reloadTimer !== null) clearTimeout(this.reloadTimer);
    for (const off of this.unsubscribe.splice(0)) off();
    this.renderer.dispose();
  }

  /**
   * The main canvas's WebGL context can be taken away: iOS WebKit keeps a handful of live contexts and drops the
   * OLDEST when another page element makes a new one, and the OS can reclaim it in the background. three prevents
   * the default (so the browser may restore it) and re-creates its own state on restore; what the rig built on the GPU
   * itself — the post chain's targets, the PMREM environment — is rebuilt here. A context that is not given back
   * within ContextRestoreMs of the page being visible is gone for good (WebKit's drop never restores), and a black
   * game is worse than a reload.
   */
  private contextLost = false;
  private reloadTimer: ReturnType<typeof setTimeout> | null = null;
  static readonly ContextRestoreMs = 3000;
  private readonly onContextLost = (e: Event): void => {
    e.preventDefault();
    consoleLogger.log("warning", "webgl: context lost");
    frameJournal.note("webgl context lost");
    breadcrumbs.loss("main context lost");
    this.contextLost = true;
    this.armReload();
  };
  private armReload(): void {
    if (this.reloadTimer !== null) clearTimeout(this.reloadTimer);
    this.reloadTimer = setTimeout(() => {
      this.reloadTimer = null;
      if (!this.contextLost) return;
      if (document.visibilityState !== "visible") { this.armReload(); return; }   // backgrounded: wait for the return
      consoleLogger.log("warning", "webgl: the context was not restored; reloading");
      reloadFor("graphics");                                  // back on the hub with a word, not the launch screen (M30 #21)
    }, SceneRig.ContextRestoreMs);
  }
  private readonly onContextRestored = (): void => {
    this.contextLost = false;
    if (this.reloadTimer !== null) { clearTimeout(this.reloadTimer); this.reloadTimer = null; }
    breadcrumbs.loss("main context lost and restored");
    this.postBuilt = null;                                   // forces a new PostFx: its targets died with the context
    this.post = null;                                        // (disposing the old one would touch the dead context's objects)
    this.sun.shadow.map = null;
    if (this.still) { this.still.remove(); this.still = null; mainRest.set(false); this.resize(); }
    this.setPost(postPlan(this.effective(), reducedVfx()));
    this.setLook(this.look);                                 // rebuilds the PMREM environment and re-applies post
    this.shadowDirty();
    // three re-uploads everything from its CPU copies on the next frame, and the ones cpuRelease.ts let go are gone:
    // drawn now they would be empty meshes and black textures. The game rebuilds the screen in place from the files
    // (main.ts `rebuildGraphics`: a battle from its suspended copy, M33); without that hook, a reload to the hub.
    if (CpuRelease.anyReleased) {
      if (this.onRebuild) { consoleLogger.log("warning", "webgl: context restored after CPU copies were released; rebuilding the screen"); this.onRebuild(); return; }
      consoleLogger.log("warning", "webgl: context restored after CPU copies were released; reloading");
      reloadFor("graphics");
    }
  };
  /** Set by the game: rebuild the current screen's GPU data from the files after a restored context (see above). */
  onRebuild: (() => void) | null = null;

  // ---- M33: resting under a full-screen overlay ------------------------------------------------------------------

  /** The frozen picture the canvas shows while the rig rests (null when live). */
  private still: HTMLCanvasElement | null = null;
  /** Whether the rig is resting (`rest`): the canvas shows a still and the frame's own GPU targets are given back. */
  get resting(): boolean { return this.still !== null; }

  /**
   * Freezes the picture as last drawn and gives back the GPU memory only a live frame needs, for a screen that covers
   * the world and needs WebGL memory of its own: the end screen, whose 3D chest is a second context with its own post
   * chain and effects atlas (M33: the most memory the game held at once; iOS closed the page there). Call right after
   * `render`, in the same task, while the drawing buffer still holds the frame.
   *
   * Given back: the post chain (two half-float screen targets, their depth, the bloom mips), the shadow map and its
   * depth, and the canvas's own drawing buffer (shrunk to a pixel). The still is a 2D canvas of the frame (4 bytes a
   * pixel). Anything that renders through the rig wakes it first (`renderFrame`), so a screen that forgets to call
   * `wake` still draws correctly — one frame later, with the targets built again.
   */
  rest(): boolean {
    if (this.still || this.contextLost || typeof document === "undefined") return false;
    const w = this.canvas.width, h = this.canvas.height;
    if (w <= 1 || h <= 1) return false;
    const still = document.createElement("canvas");
    still.width = w; still.height = h;
    still.className = "view-still";
    const g = still.getContext("2d");
    if (!g) return false;
    try { g.drawImage(this.canvas, 0, 0); } catch { return false; }
    still.style.cssText = "position:absolute;inset:0;width:100%;height:100%;display:block;pointer-events:none;";
    this.canvas.after(still);
    this.canvas.style.visibility = "hidden";
    this.still = still;
    this.post?.dispose();
    this.post = null;
    this.postBuilt = null;
    this.sun.shadow.map?.dispose();
    this.sun.shadow.map = null;                              // re-allocated by the next shadow render
    this.renderer.setSize(1, 1, false);                     // the drawing buffer: re-sized by `wake`
    mainRest.set(true);
    return true;
  }

  /** Back to live rendering (the first render after `rest` does this by itself). */
  wake(): void {
    if (!this.still) return;
    this.still.remove();
    this.still = null;
    this.canvas.style.visibility = "";
    mainRest.set(false);
    this.resize();
    this.setPost(postPlan(this.effective(), reducedVfx()));
    this.shadowDirty();
  }

  /** Opens the view on a cell (the base at level start, so the first road card is placed in sight). */
  focusCell(c: Cell, aheadZ = 0): void { this.iso.focus(c.x, c.y + aheadZ); }
  /** Double-tap: glide the view to a cell. */
  glideToCell(c: Cell): void { this.iso.glide(c.x, c.y); }
  shake(seconds?: number, strength?: number): void { this.iso.shake(seconds, strength); }

  frame(grid: Grid): void {
    if (this.dusk.bossesAlive > 0 || this.duskOn) { this.dusk.reset(); this.tickDusk(0); }   // a new map starts in the afternoon
    if (this.rareGlow.weights.active || this.rareGlow.living) { this.rareGlow.reset(); this.tickDusk(0); }   // M36: and never in a rare's night
    this.iso.frame(grid.width, grid.height);
    const cx = (grid.width - 1) / 2, cz = (grid.height - 1) / 2;
    this.sun.target.position.set(cx, 0, cz);
    this.gridRadius = Math.hypot(grid.width, grid.height) / 2;
    this.sunYaw = NaN; this.placeSun();
    this.shadowDirty();
    const half = Math.max(grid.width, grid.height) / 2 + 2;
    this.sun.shadow.camera.left = this.sun.shadow.camera.bottom = -half;
    this.sun.shadow.camera.right = this.sun.shadow.camera.top = half;
    this.sun.shadow.camera.updateProjectionMatrix();
    this.fullShadow = { cx, cz, half };
  }
  /**
   * Fits the sun's shadow camera to the ground the view actually shows, plus a margin for casters just off screen.
   *
   * It covered the whole map, so every tower, unit and prop on the map was drawn into the shadow map each frame
   * whether it was on screen or not, and the map's texels were spread over ground nobody could see. Fitted to the
   * view, off-screen casters are culled from the shadow pass and the same 2048 map is sharper where it is looked at.
   */
  private fitShadowToView(): void {
    if (!this.fullShadow) return;
    const w = this.canvas.clientWidth || window.innerWidth, h = this.canvas.clientHeight || window.innerHeight;
    const pts = [[0, 0], [w, 0], [0, h], [w, h], [w / 2, h / 2]].map(([x, y]) => this.iso.viewportToGround(x, y));
    let cx: number, cz: number, half: number;
    if (pts.some((p) => p === null)) {
      ({ cx, cz, half } = this.fullShadow);                  // a corner above the horizon: fall back to the whole map
    } else {
      const xs = pts.map((p) => p!.x), zs = pts.map((p) => p!.z);
      const minX = Math.min(...xs), maxX = Math.max(...xs), minZ = Math.min(...zs), maxZ = Math.max(...zs);
      cx = (minX + maxX) / 2; cz = (minZ + maxZ) / 2;
      // the light looks down at an angle, so its square footprint must hold the view's bounding circle
      half = Math.min(this.fullShadow.half, Math.hypot(maxX - minX, maxZ - minZ) / 2 + SceneRig.ShadowMargin);
    }
    const t = this.sun.target.position, cam = this.sun.shadow.camera;
    if (Math.abs(t.x - cx) < 0.3 && Math.abs(t.z - cz) < 0.3 && Math.abs(cam.right - half) < 0.3) return;
    t.set(cx, 0, cz);
    cam.left = cam.bottom = -half; cam.right = cam.top = half;
    cam.updateProjectionMatrix();
    this.sunYaw = NaN;                                        // placeSun re-aims the light at the new target
    this.shadowDirty();
  }
  /** Extra world units the shadow reaches past the view: a tall tower just off screen still shades the edge. */
  static readonly ShadowMargin = 2.5;
  /** The whole-map shadow box `frame` set up, used as the cap and as the fallback. */
  private fullShadow: { cx: number; cz: number; half: number } | null = null;

  /** Pixels of UI covering the top and bottom of the canvas; the map is framed in the band between. */
  setInsets(top: number, bottom: number): void { this.iso.setInsets(top, bottom); }
  /** A pinch: about the point between the fingers (client px), eased, when the gesture gives one; else at once. */
  onPinch(scale: number, clientX?: number, clientY?: number): void {
    if (clientX === undefined || clientY === undefined) { this.iso.onPinch(scale); return; }
    const r = this.canvas.getBoundingClientRect();
    this.iso.pinchAt(scale, clientX - r.left, clientY - r.top);
  }
  /** A flicked pan released at (vx, vy) px/s: the map glides on (render/cameraMotion.ts). */
  fling(vx: number, vy: number): void { this.iso.fling(vx, vy); }
  onRotate(deg: number): void { this.iso.onRotate(deg); }
  rotateEnd(): void { this.iso.rotateEnd(); }
  dragBegin(clientX: number, clientY: number): void { const r = this.canvas.getBoundingClientRect(); this.iso.dragBegin(clientX - r.left, clientY - r.top); }
  dragTo(clientX: number, clientY: number): void { const r = this.canvas.getBoundingClientRect(); this.iso.dragTo(clientX - r.left, clientY - r.top); }
  dragEnd(): void { this.iso.dragEnd(); }
  private gridRadius = 8;
  /**
   * The key light rides with the camera: it comes from the viewer's right, 45° round toward the viewer, at ~52°
   * elevation, so shadows fall diagonally sideways across the grass instead of straight back onto the next tower up
   * the road (Kevin, 2026-09-09). Re-aimed whenever the view rotates.
   */
  private sunYaw = NaN;
  /** The look currently applied, for views that need its colours. */
  get lookNow(): Look { return this.look; }

  private placeSun(): void {
    const yaw = this.iso.currentYaw;
    if (yaw === this.sunYaw) return;
    this.sunYaw = yaw;
    this.shadowDirty();
    const r = THREE.MathUtils.degToRad(yaw);
    const right = new THREE.Vector3(-Math.cos(r), 0, Math.sin(r));
    const forward = new THREE.Vector3(Math.sin(r), 0, Math.cos(r));
    const dir = right.clone().sub(forward).normalize();       // 45° between the camera's right and the camera itself
    const t = this.sun.target.position;
    const el = THREE.MathUtils.degToRad(this.look.sun.elevation);
    const dist = 14;
    this.sun.position.set(t.x + dir.x * dist * Math.cos(el), dist * Math.sin(el), t.z + dir.z * dist * Math.cos(el));
    this.fill.target.position.copy(t);
    this.fill.position.set(t.x - dir.x * 10, 6, t.z - dir.z * 10);
    const sunDir = this.sun.position.clone().sub(t).normalize();
    if (this.sky) (this.sky.material as THREE.ShaderMaterial).uniforms.uSunDir.value.copy(sunDir);
    // Both the ground's own haze and the backstop take the SKY's horizon colour. The ground ends somewhere, and
    // wherever it ends it is seen against the sky just above the horizon — matching that colour is what makes the
    // edge disappear instead of reading as a line drawn across the world.
    SurroundView.applyLook(this.look.sky);
    if (GroundMesh.hazeUniform) GroundMesh.hazeUniform.value.set(this.look.sky);
    // The props take the same colour. Leaving this to whoever built the scene meant it went stale the moment the
    // look changed, and the scene's trees hung crisp over ground that had already dissolved. The BOUNDS still belong
    // to the scene — a level's playable rectangle, the hub's island — only the colour follows the look.
    HqMaterials.hazeColor.value.set(this.look.sky);
  }

  /** Advances the camera, the shared wind clock and the fog band (a map-radius past the look point, fading to the sky). */
  update(dt: number): void {
    this.iso.update(dt);
    this.fitShadowToView();
    this.placeSun();
    if (this.sky) this.sky.position.copy(this.camera.position);
    HqMaterials.time.value += dt;
    HqMaterials.updateGlow(dt);
    SeeThrough.update(dt, this.camera);
    CloudShadows.update(dt);
    StrikeLight.update(dt);
    // the rim's sun-side bias wants the sun direction in view space, and the camera moves every frame
    this.camera.updateMatrixWorld();
    SceneRig.sunDir.subVectors(this.sun.position, this.sun.target.position).normalize();
    HqMaterials.rimSunView.value.copy(SceneRig.sunDir).transformDirection(this.camera.matrixWorldInverse);
    this.tickShadows(dt);
    this.tickDusk(dt);
    const fog = this.scene.fog as THREE.Fog;
    const look = this.iso.lookPoint();
    const d = this.camera.position.distanceTo(new THREE.Vector3(look.x, 0, look.z));
    fog.near = d + this.gridRadius * this.look.fogNear;
    fog.far = d + this.gridRadius * this.look.fogFar;
  }
  private static readonly sunDir = new THREE.Vector3();

  // -------------------------------------------------------------------------------------------------------------------
  // Look blends (Phase 2 hero moments: "boss dusk", "the sun breaks through")
  // -------------------------------------------------------------------------------------------------------------------

  private blendTo: Look | null = null;
  private blendT = 0;
  private static readonly ca = new THREE.Color();
  private static readonly cb = new THREE.Color();

  /**
   * Blends the applied look toward `to` by `t` (0 = the applied look, 1 = `to`) WITHOUT rebuilding anything: lights,
   * exposure, fog colour, the sky dome's colours, the map-edge haze, bloom and grade are lerped; the PMREM environment
   * and the materials stay as they are (`setLook` rebuilds the PMREM — a visible hitch mid-battle). Call it every frame
   * while animating (e.g. `rig.blendLook(BOSS_DUSK, ease(elapsed / 3))`), and with t = 0 to return. No allocation.
   * Glow pulse and cloud cover ride along: `HqMaterials.glowPulseAmp` → 1 + t, `CloudShadows` amount → 1 + 0.6 t.
   * The boss dusk drives this itself (`dusk`); a manual blend while a boss lives is overwritten next frame.
   */
  blendLook(to: Look, t: number): void { this.blendLooks(to, t, null, 0); }

  /**
   * Two blend targets at once, added as offsets from the applied look: value = base + wa·(a − base) + wb·(b − base).
   * The boss dusk uses it to lift the dusk (a) while the sun-break overshoot (b) rises, without a pop between them.
   */
  blendLooks(a: Look, wa: number, b: Look | null, wb: number): void {
    const ka = Math.max(0, Math.min(1, wa)), kb = b ? Math.max(0, Math.min(1, wb)) : 0;
    const B = b ?? a;
    this.blendTo = ka > 0 ? a : kb > 0 ? B : null; this.blendT = Math.max(ka, kb);
    const o = this.look;
    const L = (x: number, y: number, z: number): number => x + (y - x) * ka + (z - x) * kb;
    const C = SceneRig.ca, CA = SceneRig.cb, CB = SceneRig.cc;
    const col = (target: THREE.Color, x: number, y: number, z: number): THREE.Color => {
      C.set(x); CA.set(y); CB.set(z);
      return target.setRGB(Math.max(0, L(C.r, CA.r, CB.r)), Math.max(0, L(C.g, CA.g, CB.g)), Math.max(0, L(C.b, CA.b, CB.b)));
    };
    this.renderer.toneMappingExposure = L(o.exposure, a.exposure, B.exposure);
    col(this.sun.color, o.sun.color, a.sun.color, B.sun.color); this.sun.intensity = L(o.sun.intensity, a.sun.intensity, B.sun.intensity);
    col(this.fill.color, o.fill.color, a.fill.color, B.fill.color); this.fill.intensity = L(o.fill.intensity, a.fill.intensity, B.fill.intensity);
    col(this.hemi.color, o.hemi.sky, a.hemi.sky, B.hemi.sky); col(this.hemi.groundColor, o.hemi.ground, a.hemi.ground, B.hemi.ground);
    this.hemi.intensity = L(o.hemi.intensity, a.hemi.intensity, B.hemi.intensity);
    col((this.scene.fog as THREE.Fog).color, o.fog, a.fog, B.fog);
    // the sky dome, and everything that must match its horizon or the map edge shows as a line (see placeSun)
    if (this.sky) {
      const u = (this.sky.material as THREE.ShaderMaterial).uniforms;
      col(u.uTop.value as THREE.Color, o.skyTop ?? o.envSky, a.skyTop ?? a.envSky, B.skyTop ?? B.envSky);
      col(u.uSun.value as THREE.Color, o.envSun, a.envSun, B.envSun);
      const horizon = col(u.uHorizon.value as THREE.Color, o.sky, a.sky, B.sky);
      HqMaterials.hazeColor.value.copy(horizon);
      GroundMesh.hazeUniform?.value.copy(horizon);
      this.renderer.setClearColor(horizon);
      SurroundView.applyLook(horizon.getHex());
    }
    HqMaterials.glowPulseAmp.value = 1 + ka;
    CloudShadows.uniforms.uCloudAmount.value = 1 + 0.6 * ka;
    if (this.post) {
      this.post.setBloomParams(L(o.bloom.strength, a.bloom.strength, B.bloom.strength), L(o.bloom.threshold, a.bloom.threshold, B.bloom.threshold));
      const g = SceneRig.blendGrade, go = o.grade, ga = a.grade, gb = B.grade;
      g.contrast = L(go.contrast, ga.contrast, gb.contrast); g.saturation = L(go.saturation, ga.saturation, gb.saturation);
      g.vignette = Math.max(0, L(go.vignette, ga.vignette, gb.vignette)); g.lift = L(go.lift, ga.lift, gb.lift);
      g.shadows = col(SceneRig.cg, go.shadows, ga.shadows, gb.shadows).getHex();
      g.highlights = col(SceneRig.cg, go.highlights, ga.highlights, gb.highlights).getHex();
      this.post.setGrade(g);
    }
  }
  private static readonly cc = new THREE.Color();
  private static readonly cg = new THREE.Color();

  /**
   * The boss dusk (bossDusk.ts): the level subscribes it to the enemies view's boss events
   * (`subscribeBossDusk(enemies, rig.dusk)`); `update` turns its weights into a two-target look blend. Reset on every
   * new map (`frame`).
   */
  readonly dusk = new BossDusk();
  private duskOn = false;
  private tickDusk(dt: number): void {
    const w = this.dusk.update(dt);
    // M36: a rare's golden night (rareLook.ts) holds the look while it is up; the boss dusk waits under it
    const r = this.rareGlow.update(dt);
    if (r.active && this.rareGlow.look) {
      this.blendLooks(this.rareGlow.look, r.night, SUN_BREAK, r.sun);
      this.duskOn = true;
      return;
    }
    if (!w.active && !this.duskOn) return;
    this.blendLooks(BOSS_DUSK, w.dusk, SUN_BREAK, w.sun);     // at 0/0 this restores the applied look exactly
    this.duskOn = w.active;
  }
  /**
   * The rare spawn's look (M36, rareLook.ts): `rareGlow.spawned(rareNight(def.sky))` as it appears, `killed()` or
   * `escaped()` as it goes; this rig blends it every frame in place of the boss dusk. Reset on every new map.
   */
  readonly rareGlow = new RareGlow();

  /**
   * The colour-grade LUT (postfx.ts `setLut`): a display-space N³ `Data3DTexture`, blended in by `mix`. Kept here so
   * it survives the post chain being rebuilt on a tier change. Phase 2: per-biome grades, and animate `setLutMix` for
   * the boss-dusk crossfade. Without post (lower tiers) there is no LUT.
   */
  setLut(lut: THREE.Data3DTexture | null, mix = 1): void { this.lut = lut; this.lutMix = mix; this.post?.setLut(lut, mix); }
  setLutMix(mix: number): void { this.lutMix = mix; this.post?.setLutMix(mix); }
  private lut: THREE.Data3DTexture | null = null;
  private lutMix = 1;
  private static readonly blendGrade: Look["grade"] = { contrast: 1, saturation: 1, vignette: 0, shadows: 0xffffff, highlights: 0xffffff, lift: 0 };
  /** The look blend in effect (null when none), for views that want to follow it. */
  get lookBlend(): { to: Look; t: number } | null { return this.blendTo ? { to: this.blendTo, t: this.blendT } : null; }

  // -------------------------------------------------------------------------------------------------------------------
  // Shadow-map updates on demand (lighting review F5, partial)
  // -------------------------------------------------------------------------------------------------------------------

  /**
   * Opt-in: re-render the shadow map only when something changed, instead of every frame. OFF by default, because
   * the rig cannot tell on its own what moves. The hub and the level turn it on: units other than bosses do not cast
   * (their ground rings ground them, unitFx.ts), critters and projectiles never did, and while a boss is alive the map
   * is refreshed at BossShadowHz (`tickShadows`).
   *
   * In manual mode the map is re-rendered: when the view's shadow box moves (fitShadowToView), on a new map (`frame`),
   * on `shadowDirty()` (build, sell, tier change, anything that adds or removes a caster), and at `animatedHz` while
   * `animatedCasters > 0` (views count their animating casters up and down); `ShadowCadence.EveryFrame` refreshes on
   * every rendered frame.
   */
  setShadowUpdates(mode: "auto" | "manual", animatedHz = 10): void {
    this.shadowManual = mode === "manual";
    this.shadowHz = animatedHz;
    this.renderer.shadowMap.autoUpdate = !this.shadowManual;
    this.shadowDirty();
  }
  /**
   * The battle's shadow refresh rate in manual mode: faster than the hub's idle 10 Hz because the towers' weapons turn
   * to aim and recoil; still a quarter of the per-frame cost at 60 fps.
   */
  static readonly BattleShadowHz = 15;
  /** While a boss (the one unit that casts) is alive: a slow walker's shadow trails it by at most a frame or two. */
  static readonly BossShadowHz = 30;
  /** Changes the manual-mode refresh rate without a refresh (the hub follows the quality tier: hubShadowHz). */
  setShadowHz(hz: number): void { this.shadowHz = hz; }
  /** The render-quality tier in effect (its name: "ultra" … "lowest"). */
  get tierName(): string { return this.effective().name; }
  /** Marks the shadow map stale: it is re-rendered on the next frame (manual mode; harmless in auto). */
  shadowDirty(): void { this.renderer.shadowMap.needsUpdate = true; }
  /** How many casters are animating right now; while above 0, manual mode refreshes at `animatedHz`. */
  animatedCasters = 0;
  private shadowManual = false;
  private shadowHz = 10;
  /** Carries the remainder between refreshes, so a rate lands on its frames (shadowCadence.ts). */
  private readonly shadowClock = new ShadowCadence();
  private tickShadows(dt: number): void {
    if (!this.shadowManual) return;
    // a boss casts and walks (the grunts do not cast, unitFx.ts): faster, so its shadow does not step behind it
    const boss = this.dusk.bossesAlive > 0 || this.rareGlow.living;
    if (this.animatedCasters <= 0 && !boss) { this.shadowClock.reset(); return; }
    const hz = boss ? Math.max(this.shadowHz, SceneRig.BossShadowHz) : this.shadowHz;
    if (this.shadowClock.step(dt, hz)) this.shadowDirty();
  }

  /** A bare render loop for scenes without a controller (the readability sheet). */
  frameLoop(): void { const tick = () => { this.iso.update(1 / 60); this.render(); requestAnimationFrame(tick); }; tick(); }
  render(): void { CpuRelease.asMain(() => this.renderFrame()); }
  private renderFrame(): void {
    if (this.still) this.wake();
    const t0 = performance.now();
    this.checkShaderHealth();
    this.renderer.info.reset();
    if (this.post) this.post.render();
    else {
      // no post chain (only before the first tier applies): the exposure punch rides on the renderer's own exposure
      const punch = PostFx.punchGain(t0), base = this.renderer.toneMappingExposure;
      if (punch !== 1) this.renderer.toneMappingExposure = base * punch;
      this.renderer.render(this.scene, this.camera);
      this.renderer.toneMappingExposure = base;
    }
    const t1 = performance.now();
    const f = this.frameStats, r = this.renderer.info.render;
    f.cpuMs = t1 - t0; f.calls = r.calls; f.triangles = r.triangles; f.frames++;
    // Inside a requestAnimationFrame callback the document timeline's time is the frame's start, so t0 minus it is
    // the JavaScript this frame ran before rendering (simulation, views, UI), which tells the governor whether the
    // frame is CPU-bound. Not the render's own time: a GPU that is behind blocks inside the GL calls, and counting
    // that as CPU would hide exactly the fill-bound frames the governor is for.
    const start = typeof document !== "undefined" ? document.timeline?.currentTime : null;
    const frameStart = typeof start === "number" && start <= t0 ? start : t0;
    this.quality?.frame(frameStart, t0 - frameStart);
  }

  /** The last rendered frame, read by the performance overlay: CPU time spent submitting it, draw calls, triangles. */
  readonly frameStats = { cpuMs: 0, calls: 0, triangles: 0, frames: 0 };

  // Performance switches for the debug overlay: each one is a suspect when a phone lags.
  get bloomOn(): boolean { return this.bloomWanted; }
  /** The governor's dynamic-resolution scale within the tier (1 = the tier's full resolution). */
  get renderScale(): number { return this.quality?.scale ?? 1; }
  /** The antialiasing the live post chain was built with (the perf overlay / captures can show it): null without post. */
  get postAa(): { msaa: number; fxaa: boolean } | null { return this.post ? { msaa: this.post.samples, fxaa: this.postFxaa } : null; }
  get pixelRatio(): number { return this.renderer.getPixelRatio(); }
  /** The perf overlay's res button: overrides the tier's ratio until the next tier apply. */
  setPixelRatio(r: number): void { if (this.quality) this.quality.locked = true; this.renderer.setPixelRatio(r); this.resize(); }
  get shadowsOn(): boolean { return this.renderer.shadowMap.enabled; }
  setShadows(on: boolean): void {
    this.renderer.shadowMap.enabled = on;
    this.shadowDirty();
    this.sun.castShadow = on;
    // shadow receiving is compiled into each material's program
    this.scene.traverse((o) => {
      const m = (o as THREE.Mesh).material;
      if (m) for (const x of Array.isArray(m) ? m : [m]) x.needsUpdate = true;
    });
  }

  /** Screen point → grid cell via the y=0 plane. */
  /**
   * Screen point → the grid cell under it, on the actual terrain. This used the y=0 plane, but the ground rolls a
   * metre and more between benches, so a tap on a raised cell landed on the cell behind it (and on a low one, in
   * front): the finger was on the right tile and the game picked its neighbour.
   */
  pickCell(clientX: number, clientY: number, grid: Grid): Cell | null {
    const rect = this.canvas.getBoundingClientRect();
    const hit = this.iso.viewportToTerrain(clientX - rect.left, clientY - rect.top, groundAt);
    if (hit === null) return null;
    const cell = new Cell(Math.round(hit.x), Math.round(hit.z));
    return grid.inBounds(cell) ? cell : null;
  }

  /** A cell's centre on the terrain → CSS pixel coordinates. */
  cellOnScreen(c: Cell): { x: number; y: number } {
    const rect = this.canvas.getBoundingClientRect();
    const p = this.iso.worldToViewport(c.x, groundAt(c.x, c.y), c.y);
    return { x: rect.left + p.x, y: rect.top + p.y };
  }

  /** World cell → CSS pixel coordinates (for tests and debug tooling). */
  cellToScreen(c: Cell): { x: number; y: number } {
    const rect = this.canvas.getBoundingClientRect();
    const p = this.iso.cellToViewport(c);
    return { x: rect.left + p.x, y: rect.top + p.y };
  }

  private resize = (): void => {
    if (this.still) return;                                  // resting: the still keeps its picture; `wake` re-sizes
    const w = this.canvas.clientWidth || window.innerWidth;
    const h = this.canvas.clientHeight || window.innerHeight;
    this.renderer.setSize(w, h, false);
    this.post?.resize(w, h);
    this.iso.setViewport(w, h);
  };
}
